import React, { useState } from 'react';
import {
  Alert,
  Button,
  CheckboxField,
  Flex,
  Heading,
  SelectField,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import Loadingmini from '../components/loadingmini';
import { api3Delete, api3Get, api3Post } from '../api/api3Client';

export default function AdminUsers() {
  const [query, setQuery] = useState('');
  const [excludeStale, setExcludeStale] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [enrollFleetId, setEnrollFleetId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  async function runSearch() {
    setLoading(true);
    setError('');
    setDetail(null);
    setSelectedId(null);
    try {
      const res = await api3Get('/admin/portal/parties/search', {
        q: query,
        limit: 50,
        excludeStale,
      });
      setItems(res.items || []);
    } catch (e) {
      setError(e.message || String(e));
      setItems([]);
    }
    setLoading(false);
  }

  async function loadDetail(id) {
    setLoading(true);
    setError('');
    setSelectedId(id);
    try {
      const res = await api3Get(`/admin/portal/parties/${id}`);
      setDetail(res.party);
    } catch (e) {
      setError(e.message || String(e));
      setDetail(null);
    }
    setLoading(false);
  }

  async function executeConfirm() {
    if (!confirm || !detail) return;
    setActionLoading(true);
    setError('');
    try {
      const id = detail.id;
      if (confirm.type === 'enroll') {
        await api3Post(`/parties/${id}/fleets`, {
          fleetId: Number(confirm.fleetId),
          role: 'driver',
        });
      } else if (confirm.type === 'unenroll') {
        await api3Delete(`/parties/${id}/fleets/${confirm.fleetId}`);
      } else if (confirm.type === 'delete') {
        await api3Delete(`/parties/${id}`);
      }
      setConfirm(null);
      await loadDetail(id);
    } catch (e) {
      setError(e.message || String(e));
    }
    setActionLoading(false);
  }

  const activeFleets =
    detail?.fleetMemberships?.filter((f) => f.status === 'active') || [];

  return (
    <View padding="1rem">
      <Heading level={3}>Admin: User Management</Heading>
      <Flex direction="row" gap="0.5rem" alignItems="flex-end" wrap="wrap">
        <TextField
          label="Search (phone, email, cognito, name, id)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
        />
        <Button onClick={runSearch} isLoading={loading}>
          Search
        </Button>
        <CheckboxField
          label="Hide deleted users"
          name="excludeStale"
          checked={excludeStale}
          onChange={(e) => setExcludeStale(e.target.checked)}
        />
      </Flex>

      {error ? (
        <Alert variation="error" isDismissible={false} marginTop="1rem">
          {error}
        </Alert>
      ) : null}

      {loading && !detail ? <Loadingmini /> : null}

      <Flex direction="row" gap="1rem" marginTop="1rem" wrap="wrap">
        <View flex="1" minWidth="280px">
          <Heading level={5}>Results</Heading>
          {items.length === 0 ? (
            <Text>No results</Text>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th align="left">ID</th>
                  <th align="left">Name</th>
                  <th align="left">Phone</th>
                  <th align="left">Email</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.id}
                    style={{
                      cursor: 'pointer',
                      background: selectedId === row.id ? '#eef6ff' : undefined,
                    }}
                    onClick={() => loadDetail(row.id)}
                  >
                    <td>{row.id}</td>
                    <td>{row.displayName}</td>
                    <td>{row.phone}</td>
                    <td>{row.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </View>

        {detail ? (
          <View flex="1" minWidth="320px">
            <Heading level={5}>User {detail.id}</Heading>
            <Text>Name: {detail.displayName}</Text>
            <Text>Phone: {detail.phone}</Text>
            <Text>Email: {detail.email}</Text>
            <Text>Cognito: {detail.cognitoId || '—'}</Text>
            <Text>MVR status: {detail.checkrMvrStatus || '—'}</Text>

            <Heading level={6} marginTop="1rem">
              Fleet memberships
            </Heading>
            {detail.fleetMemberships?.length ? (
              <ul>
                {detail.fleetMemberships.map((f) => (
                  <li key={`${f.fleetId}-${f.status}`}>
                    {f.fleetCode || f.fleetId} — {f.status}
                    {f.status === 'active' ? (
                      <Button
                        size="small"
                        variation="warning"
                        marginLeft="0.5rem"
                        onClick={() =>
                          setConfirm({
                            type: 'unenroll',
                            fleetId: f.fleetId,
                            label: `Unenroll from fleet ${f.fleetCode || f.fleetId}?`,
                          })
                        }
                      >
                        Unenroll
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <Text>No fleet rows</Text>
            )}

            <Flex direction="row" gap="0.5rem" marginTop="1rem" alignItems="flex-end">
              <TextField
                label="Enroll fleet ID"
                value={enrollFleetId}
                onChange={(e) => setEnrollFleetId(e.target.value)}
              />
              <Button
                onClick={() =>
                  setConfirm({
                    type: 'enroll',
                    fleetId: enrollFleetId,
                    label: `Enroll user ${detail.id} in fleet ${enrollFleetId}?`,
                  })
                }
                isDisabled={!enrollFleetId}
              >
                Enroll
              </Button>
            </Flex>

            <Button
              variation="destructive"
              marginTop="1rem"
              isDisabled={detail.hasActiveFleetMembership}
              onClick={() =>
                setConfirm({
                  type: 'delete',
                  label: `Permanently delete user ${detail.id}? All fleet memberships must be inactive first.`,
                })
              }
            >
              Delete user
            </Button>
            {detail.hasActiveFleetMembership ? (
              <Text color="red">Unenroll all active fleets before delete.</Text>
            ) : null}

            {confirm ? (
              <Alert variation="warning" marginTop="1rem">
                <Text>{confirm.label}</Text>
                <Flex direction="row" gap="0.5rem" marginTop="0.5rem">
                  <Button
                    variation="primary"
                    onClick={executeConfirm}
                    isLoading={actionLoading}
                  >
                    Confirm
                  </Button>
                  <Button onClick={() => setConfirm(null)} isDisabled={actionLoading}>
                    Cancel
                  </Button>
                </Flex>
              </Alert>
            ) : null}
          </View>
        ) : null}
      </Flex>
    </View>
  );
}
